import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import dashboardService from '@api/v1/dashboardService'
import ReportChart from '@components/reports/ReportChart'
import LoadingSpinner from '@components/common/LoadingSpinner'
import useReadState from '@hooks/useReadState'
import TileTable from './TileTable'

// CB uses "*CBNone*" as the category value for "no value" — show it friendlier.
const cleanName = (n) => (n === '*CBNone*' ? '(None)' : n)

// Fills its (fixed-height) grid tile as a flex column: the title stays pinned
// and the body scrolls INSIDE the white card, so long lists no longer spill
// below the card (and drag "View all" out with them).
const Shell = ({ title, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 h-full flex flex-col overflow-hidden">
    {title && <h3 className="text-sm font-semibold text-gray-900 px-5 pt-5 pb-3 shrink-0 truncate">{title}</h3>}
    <div className={`flex-1 min-h-0 overflow-auto px-5 ${title ? 'pb-5' : 'py-5'}`}>{children}</div>
  </div>
)

const Empty = ({ note }) => (
  <div className="h-24 flex items-center justify-center text-center px-4">
    <span className="text-xs text-gray-400">{note}</span>
  </div>
)
const Busy = () => <div className="h-24 flex items-center justify-center"><LoadingSpinner message="Loading…" /></div>

/*
 * The tile equivalent of the `blocked` panel on the Report Center (CC-53132).
 *
 * A read react-query PAUSED never happened at all — `queryFn` is not called, so
 * there is no request, no error and no spinner to give it away (see
 * `@api/v1/readState`). On a dashboard that lands harder than it looks: `money()`
 * turns `undefined` into "$0" and an absent list reads "Nothing for today", so
 * the tile makes a confident financial statement about data nobody asked the
 * server for.
 *
 * Tiles are too small for a paragraph, so this is one line plus a way out. The
 * line is supplied per tile on purpose: "$0" and "nothing today" are different
 * lies and have to be denied in different words. `role="alert"` so it is
 * announced — the user has to know the tile is not describing their firm.
 */
const Unreachable = ({ note, onRetry }) => (
  <div role="alert" className="h-24 flex flex-col items-center justify-center gap-1.5 px-3 text-center">
    <span className="text-xs text-amber-800">{note}</span>
    <button
      type="button"
      onClick={onRetry}
      className="rounded-lg border border-amber-300 bg-white px-2 py-0.5 text-xs font-medium text-amber-900 hover:bg-amber-50"
    >
      Try again
    </button>
  </div>
)

// .NET FormatString ("N0", "C", "P1", …) → a display string.
const formatFormula = (value, format = '') => {
  if (value == null || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  if (/^C/i.test(format)) return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })
  if (/^P/i.test(format)) return n.toLocaleString('en-US', { style: 'percent', maximumFractionDigits: 1 })
  const dec = /^N(\d)/i.exec(format)?.[1]
  return n.toLocaleString('en-US', dec != null ? { minimumFractionDigits: +dec, maximumFractionDigits: +dec } : {})
}
const money = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

// --- Chart widget: fetch its data (legacy DataSet → chart-ready rows) and render. ---
const ChartWidget = ({ chartId, title }) => {
  const chartQuery = useQuery({
    queryKey: ['dashboard-chart', chartId],
    queryFn: () => dashboardService.chartData(chartId),
    enabled: !!chartId, staleTime: 5 * 60_000, retry: false,
  })
  const { data, refetch } = chartQuery
  const chartState = useReadState('dashboard-chart', chartQuery)
  if (chartState === 'failed') return <Shell title={title}><Empty note="Couldn’t load this chart." /></Shell>
  if (chartState === 'blocked') {
    return (
      <Shell title={title}>
        <Unreachable note="Can’t reach the server, so this chart hasn’t loaded. This is not “no data”." onRetry={refetch} />
      </Shell>
    )
  }
  // `idle` rides with `loading`: this tile is only rendered when `chartId` is
  // truthy (see the switch below), so `enabled` can never be false here and a
  // spinner cannot get stranded.
  if (chartState !== 'ready') return <Shell title={title}><div className="h-64 flex items-center justify-center"><LoadingSpinner message="Loading chart…" /></div></Shell>
  if (data?.webChartUnsupported) return <Shell title={title}><Empty note="This chart type isn’t available here yet — open it in the classic dashboard." /></Shell>
  // Only reachable once the read completed, so an empty `rows` here really is an
  // empty period rather than a read that never happened.
  const rows = (data?.rows || []).map((r) => ({ ...r, name: cleanName(r.name) }))
  if (rows.length === 0) return <Shell title={title}><Empty note="No data for this period." /></Shell>
  return (
    <ReportChart
      data={rows}
      categoryKey="name"
      valueKey={data.valueKey || 'value'}
      valueName={data.name || title}
      valueFormat="number"
      initialType={data.kind || 'bar'}
    />
  )
}

// --- KPI tile (SystemFormula): label + computed value. ---
const KpiWidget = ({ formulaId, title }) => {
  const kpiQuery = useQuery({
    queryKey: ['dashboard-formula', formulaId],
    queryFn: () => dashboardService.formula(formulaId),
    enabled: !!formulaId, staleTime: 5 * 60_000, retry: false,
  })
  const { data, refetch } = kpiQuery
  const kpiState = useReadState('dashboard-formula', kpiQuery)
  return (
    <Shell title={data?.name || title || 'Metric'}>
      {kpiState === 'failed' ? <Empty note="Couldn’t load this metric." />
        : kpiState === 'blocked'
          ? <Unreachable note="Can’t reach the server, so this metric hasn’t loaded. The tile is blank because we don’t know the number." onRetry={refetch} />
          /* `idle` gets its OWN state here — it must not join either neighbour.
             `enabled: !!formulaId` plus a switch below that renders this tile
             without checking `systemFormulaId` means a widget row missing that
             id switches the query off permanently: folded into the spinner it
             would spin forever, and the value branch printed "—", which is
             exactly what a genuinely null metric shows. */
          : kpiState === 'idle' ? <Empty note="This tile has no metric configured." />
            : kpiState !== 'ready' ? <Busy />
              : (
                <div className="h-24 flex flex-col items-center justify-center">
                  <span className="text-4xl font-display text-cb-navy tabular-nums">{formatFormula(data?.value, data?.format)}</span>
                  {data?.description && <span className="text-xs text-gray-400 mt-1 text-center">{data.description}</span>}
                </div>
              )}
    </Shell>
  )
}

// When-label for an activity: appointments show the start time (or "All day");
// tasks show the due date (e.g. "Jul 31") so upcoming/overdue items read clearly.
// QA 7/9: the legacy feed returns recurring appointments across MULTIPLE days —
// a bare time made the (correct) timestamp sort look shuffled, so anything not
// today carries its date too ("Jul 5 · 2:00 PM").
const activityWhen = (it, kind, now = new Date()) => {
  if (!it?.date) return ''
  const d = new Date(it.date)
  if (isNaN(d.getTime())) return ''
  if (kind === 'appointments') {
    const time = it.hasTime ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'All day'
    if (d.toDateString() === now.toDateString()) return time
    return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${time}`
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// --- Today's tasks / appointments: a compact tabular tile. Column headers are
// click-to-sort (CC-49332) — ascending first, date-aware via the raw timestamp.
const ActivitiesWidget = ({ kind, title }) => {
  const activitiesQuery = useQuery({
    queryKey: ['dashboard-activities', kind],
    queryFn: () => dashboardService.activities(kind),
    staleTime: 60_000, retry: false,
  })
  // No `= []` default: that fallback is what made a paused read ("we never
  // asked") indistinguishable from a clear day ("we asked, there is nothing").
  // `items` is only dereferenced inside the `ready` branch below.
  const { data: items, refetch } = activitiesQuery
  const activitiesState = useReadState(`dashboard-activities:${kind}`, activitiesQuery)
  const columns = [
    {
      key: 'when',
      label: kind === 'appointments' ? 'Time' : 'Due',
      // Sort by the real timestamp, not the "Jul 31" / "9:00 AM" display string.
      sortValue: (it) => {
        const t = new Date(it?.date).getTime()
        return Number.isNaN(t) ? null : t
      },
      render: (it) => (
        <span className="text-xs font-semibold text-cb-accent-text tabular-nums whitespace-nowrap">
          {activityWhen(it, kind) || '—'}
        </span>
      ),
    },
    {
      key: 'name',
      label: 'Item',
      render: (it) => <span className="text-sm text-gray-800 leading-snug">{it.name || '(untitled)'}</span>,
    },
    {
      key: 'matter',
      label: 'Matter',
      render: (it) => <span className="text-xs text-gray-400">{it.matter || '—'}</span>,
    },
  ]
  return (
    <Shell title={title}>
      {activitiesState === 'failed' ? <Empty note="Couldn’t load." />
        : activitiesState === 'blocked'
          ? <Unreachable note="Can’t reach the server, so today’s items haven’t loaded. This is not an empty day." onRetry={refetch} />
          /* `idle` rides with `loading`: this query has no `enabled` option, so it
             cannot be switched off and the spinner cannot get stranded. */
          : activitiesState !== 'ready' ? <Busy />
            : items.length === 0 ? <Empty note="Nothing for today." />
              /* rowKey: the legacy feed can repeat the same activity id across
                 recurrence dates (QA 7/9) — a bare r.id collides React keys. */
              : <TileTable label={title} columns={columns} rows={items} rowKey={(it) => `${it.id}|${it.date}`} defaultSort={{ key: 'when', desc: false }} />}
      <div className="pt-2 mt-2 border-t border-gray-100">
        <Link to="/calendar" className="btn-cb-tertiary">
          View all →
        </Link>
      </div>
    </Shell>
  )
}

// --- Production & origination summary tile — sortable metric/amount table
// (CC-49332); the YTD total stays pinned below, outside the sortable rows. ---
const PoWidget = ({ title }) => {
  const poQuery = useQuery({
    queryKey: ['dashboard-po', 'ytd'],
    queryFn: () => dashboardService.productionOrigination('ytd'),
    staleTime: 5 * 60_000, retry: false,
  })
  const { data, refetch } = poQuery
  /*
   * The worst site in this file, and the reason CC-53132 exists. Every figure
   * below is `Number(data?.x || 0)` / `money(undefined)`, i.e. "$0" — so a paused
   * read rendered a complete, un-annotated fee statement of ZERO ("Fees —
   * originator $0 … Total (YTD) $0") with no spinner and no error to hint that
   * nothing had been read. Fees of zero is a number a partner acts on. Nothing
   * below the gate may render until the read is `ready`.
   */
  const poState = useReadState('dashboard-po', poQuery)
  const rows = [
    { id: 'originator', metric: 'Fees — originator', amount: Number(data?.feesByOriginator || 0) },
    { id: 'timekeeper', metric: 'Fees — timekeeper', amount: Number(data?.feesByTimekeeper || 0) },
    { id: 'respAttorney', metric: 'Fees — resp. attorney', amount: Number(data?.feesByResponsibleAttorney || 0) },
  ]
  const columns = [
    { key: 'metric', label: 'Metric', render: (r) => <span className="text-gray-500">{r.metric}</span> },
    { key: 'amount', label: 'Amount', align: 'right', render: (r) => <span className="text-gray-900 tabular-nums">{money(r.amount)}</span> },
  ]
  return (
    <Shell title={title || 'Production & origination'}>
      {poState === 'failed' ? <Empty note="Couldn’t load." />
        : poState === 'blocked'
          ? <Unreachable note="Can’t reach the server, so these fees haven’t loaded. This is not $0." onRetry={refetch} />
          /* `idle` rides with `loading`: this query has no `enabled` option, so
             it cannot be switched off and the spinner cannot get stranded. */
          : poState !== 'ready' ? <Busy />
            : (
              <div className="text-sm">
                <TileTable label={title || 'Production & origination'} columns={columns} rows={rows} />
                <div className="pt-1.5 mt-1 border-t border-gray-100 flex items-center justify-between font-semibold text-cb-navy">
                  <span>Total (YTD)</span><span className="tabular-nums">{money(data?.totalCombined)}</span>
                </div>
              </div>
            )}
    </Shell>
  )
}

const DashboardWidget = ({ widget }) => {
  switch (widget?.type) {
    case 'chart':
      return widget.chart?.chartId ? <ChartWidget chartId={widget.chart.chartId} title={widget.chart.name} /> : null
    case 'kpi':
      return <KpiWidget formulaId={widget.systemFormulaId} />
    case 'tasks':
      return <ActivitiesWidget kind="tasks" title="Today's tasks" />
    case 'appointments':
      return <ActivitiesWidget kind="appointments" title="Today's appointments" />
    case 'productionOrigination':
      return <PoWidget />
    default:
      // list / formulaList / unknown — labeled placeholder.
      return (
        <Shell title="Widget">
          <div className="h-24 flex items-center justify-center">
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2.5 py-1">Coming soon</span>
          </div>
        </Shell>
      )
  }
}

export default DashboardWidget
